import { OverlayPanel } from "primereact/overlaypanel";
import React, { useRef } from "react";

import { LinkedCampaign } from "@/interfaces/customization-form.interface";
import { buildCampaignDetailUrl } from "@/utils/store-context";

interface CampaignBadgesProps {
  campaigns?: LinkedCampaign[];
}

const PRIMARY_COLOR = {
  bg: "bg-purple-50",
  text: "text-purple-700",
  border: "border-purple-100",
  hover: "hover:bg-purple-100",
  subtitle: "text-purple-400",
};

const CampaignBadges: React.FC<CampaignBadgesProps> = ({ campaigns = [] }) => {
  const overlayRef = useRef<OverlayPanel>(null);

  const handleCampaignClick = (
    e: React.MouseEvent,
    campaign: LinkedCampaign,
  ) => {
    e.stopPropagation();
    window.open(buildCampaignDetailUrl(campaign._id, campaign.store), "_blank");
  };

  const renderContent = () => {
    if (campaigns.length === 0) {
      return (
        <div className="flex w-[120px] items-center justify-center rounded-md border border-gray-100 bg-gray-50/50 px-2.5 py-1">
          <span className="text-[11px] font-medium text-gray-400 italic">
            No campaigns
          </span>
        </div>
      );
    }

    return (
      <>
        <div
          className="group flex w-[120px] cursor-pointer items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 transition-all hover:border-purple-300 hover:bg-purple-50"
          onClick={(e) => overlayRef.current?.toggle(e)}
        >
          <span className="text-xs font-semibold text-gray-600 group-hover:text-purple-700">
            {campaigns.length}{" "}
            {campaigns.length > 1 ? "Campaigns" : "Campaign"}
          </span>
          <i className="pi pi-chevron-down text-[10px] text-gray-400 transition-transform group-hover:text-purple-500"></i>
        </div>

        <OverlayPanel ref={overlayRef} style={{ width: "300px" }}>
          <div className="flex flex-col gap-2">
            <h4 className="text-sm font-semibold text-gray-700">
              All Linked Campaigns ({campaigns.length})
            </h4>
            <div className="flex max-h-60 flex-col gap-1.5 overflow-y-auto">
              {campaigns.map((campaign) => {
                const colors = PRIMARY_COLOR;
                return (
                  <div
                    key={campaign._id}
                    className={`group cursor-pointer rounded-md border px-3 py-2 text-xs transition-all ${colors.bg} ${colors.text} ${colors.border} ${colors.hover}`}
                    onClick={(e) => handleCampaignClick(e, campaign)}
                  >
                    <div className="flex flex-col h-full">
                      <div className="font-medium truncate mb-0.5">
                        <span className="border-b border-transparent group-hover:border-current pb-[1px]">
                          {campaign.title || "Untitled"}
                        </span>
                      </div>
                      {campaign.store && (
                        <div
                          className={`mt-auto flex items-end gap-1 text-[10px] opacity-80 ${colors.subtitle}`}
                        >
                          <div className="flex items-end gap-1 border-b border-transparent group-hover:border-current pb-[1px]">
                            <i className="pi pi-shop text-[9px] mb-[1px]"></i>
                            <span className="truncate">{campaign.store}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </OverlayPanel>
      </>
    );
  };

  return <div className="flex justify-start">{renderContent()}</div>;
};

export default CampaignBadges;
